export type FracEvalLike = {
  top: number;
  bottom: number;
  grade: string;
  score?: number;
};

export type FracStageLike = {
  stage_number: number;
  top_perf: number;
  bottom_perf: number;
  fluid_volume: number;
  proppant_mass: number;
};

export type FracDesignLike = {
  cluster_per_stage: number;
  cluster_spacing?: number;
  stages?: FracStageLike[];
};

export type ReservoirType = 'shale' | 'tight_sand' | 'carbonate' | 'coalbed';
export type DesignIntent = 'balanced' | 'dense_cut' | 'long_fracture' | 'risk_control' | 'cost_control';

export type Range = {
  min: number;
  max: number;
};

export type ScoringTemplate = {
  reservoirType: ReservoirType;
  intent: DesignIntent;
  label: string;
  stageLength: Range;
  clusters: Range;
  clusterSpacing: Range;
  fluidIntensity: Range;
  proppantIntensity: Range;
  maxClusterCoverageRatio: number;
};

export type TemplateBasis = {
  title: string;
  detail: string;
};

export type ScoringConfig = {
  reservoirType: ReservoirType;
  intent: DesignIntent;
  custom?: Partial<Omit<ScoringTemplate, 'reservoirType' | 'intent' | 'label'>>;
};

export type ScoreBreakdown = {
  stageLength: number;
  clusterSpacing: number;
  sweetSpot: number;
  stress: number | null;
  fluidSand: number;
};

export type StageCheck = {
  score: number;
  label: string;
  color: string;
  bg: string;
  issues: string[];
  suggestions: string[];
  gradeMix: string[];
  stageLength: number;
  clusterSpan: number;
  fluidIntensity: number;
  proppantIntensity: number;
  template: ScoringTemplate;
  basis: TemplateBasis[];
  breakdown: ScoreBreakdown;
};

export type PlanScore = {
  score: number;
  label: string;
  color: string;
  bg: string;
  checks: StageCheck[];
  breakdown: ScoreBreakdown;
  summary: string[];
  template: ScoringTemplate;
  intentDistribution: Partial<Record<DesignIntent, number>>;
};

export const RESERVOIR_LABELS: Record<ReservoirType, string> = {
  shale: '页岩/页岩气',
  tight_sand: '致密砂岩',
  carbonate: '碳酸盐岩',
  coalbed: '煤层气',
};

export const INTENT_LABELS: Record<DesignIntent, string> = {
  balanced: '均衡设计',
  dense_cut: '密切割',
  long_fracture: '长缝导向',
  risk_control: '风险控制',
  cost_control: '控成本',
};

const GRADE_RANK: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4 };

const RESERVOIR_BASIS: Record<ReservoirType, TemplateBasis[]> = {
  shale: [
    { title: '论文依据', detail: '参考甜点评价、应力阴影和多簇进液/支撑剂分配研究：页岩段簇划分应优先保证同段甜点、应力和脆性相近，过密簇距会提高应力阴影和内簇低效风险。' },
    { title: '行业标准依据', detail: '参考页岩油气甜点评价、资料录取、套管适用性、水资源保护和压裂用水输送等标准思路：标准主要给安全、资料和工程边界，不直接给固定簇距。' },
    { title: '软件模块参考', detail: '参考 StageOpt/ResFrac/Kinetix/Meyer/XSite/FracPT 的模块思路：由甜点、岩石力学、应力阴影、簇间流体与支撑剂分配共同约束段簇设计。' },
  ],
  tight_sand: [
    { title: '论文依据', detail: '致密砂岩段簇设计更强调有效砂体连续性、渗透率差异、隔夹层和应力分层，不能简单照搬页岩密切割参数。' },
    { title: '行业标准依据', detail: '按储层改造设计、施工安全、压力控制和资料录取要求设置边界条件，结合测井解释、试井和生产资料校核参数。' },
    { title: '软件模块参考', detail: '参考商业软件按砂体品质、应力屏障、缝网复杂度和压裂规模联动调整段长、簇数、液量和砂量的流程。' },
  ],
  carbonate: [
    { title: '论文依据', detail: '碳酸盐岩非均质性强，孔洞、裂缝和断层影响显著，段簇设计应避免过密布簇导致低效进液或沟通高风险通道。' },
    { title: '行业标准依据', detail: '优先满足酸压/压裂施工安全、井筒完整性、压力监测、异常风险控制和资料录取要求，再进行局部段簇优化。' },
    { title: '软件模块参考', detail: '参考 XSite、ResFrac 等离散裂缝/地质力学模拟思路，对裂缝发育段、断层邻近段采用单段特殊设计。' },
  ],
  coalbed: [
    { title: '论文依据', detail: '煤层气储层强度低、割理发育且易出煤粉，段簇设计更关注低伤害、控砂和排采适应性。' },
    { title: '行业标准依据', detail: '按煤层气压裂施工、排采和安全环保要求控制液砂强度与压力窗口，避免过强改造。' },
    { title: '软件模块参考', detail: '参考软件中按储层类型切换默认模板的做法，对煤层气使用较低液砂强度和较保守簇数。' },
  ],
};

const RESERVOIR_GUARDRAILS: Record<ReservoirType, {
  minEngineeringClusterSpacing: number;
  maxEngineeringCoverageRatio: number;
  maxFluidIntensity: number;
  maxProppantIntensity: number;
}> = {
  shale: {
    minEngineeringClusterSpacing: 7,
    maxEngineeringCoverageRatio: 0.92,
    maxFluidIntensity: 55,
    maxProppantIntensity: 4.5,
  },
  tight_sand: {
    minEngineeringClusterSpacing: 10,
    maxEngineeringCoverageRatio: 0.88,
    maxFluidIntensity: 48,
    maxProppantIntensity: 4.0,
  },
  carbonate: {
    minEngineeringClusterSpacing: 14,
    maxEngineeringCoverageRatio: 0.82,
    maxFluidIntensity: 42,
    maxProppantIntensity: 3.4,
  },
  coalbed: {
    minEngineeringClusterSpacing: 10,
    maxEngineeringCoverageRatio: 0.82,
    maxFluidIntensity: 30,
    maxProppantIntensity: 2.2,
  },
};

const INTENT_BASIS: Record<DesignIntent, string> = {
  balanced: '均衡设计以储层连续性和施工可控性为主，优先让同段甜点和力学性质相近。',
  dense_cut: '密切割用于甜点较好且需要提高改造覆盖率的井段，通常缩短段长、增加簇数并减小簇间距。',
  long_fracture: '长缝导向用于追求缝长和改造体积的设计，通常适当增加段长和簇距，并提高液量强度。',
  risk_control: '风险控制用于低品质或段内非均质性较强的井段，优先缩短段长、减少簇间竞争并降低液砂强度。',
  cost_control: '控成本以减少施工复杂度和材料消耗为目标，通常放宽段长、降低簇数和液砂强度。',
};

const BASE_TEMPLATES: Record<ReservoirType, Omit<ScoringTemplate, 'reservoirType' | 'intent' | 'label'>> = {
  shale: {
    stageLength: { min: 50, max: 90 },
    clusters: { min: 4, max: 8 },
    clusterSpacing: { min: 8, max: 18 },
    fluidIntensity: { min: 22, max: 45 },
    proppantIntensity: { min: 1.8, max: 3.8 },
    maxClusterCoverageRatio: 0.9,
  },
  tight_sand: {
    stageLength: { min: 60, max: 120 },
    clusters: { min: 3, max: 6 },
    clusterSpacing: { min: 15, max: 30 },
    fluidIntensity: { min: 18, max: 36 },
    proppantIntensity: { min: 1.4, max: 3.2 },
    maxClusterCoverageRatio: 0.85,
  },
  carbonate: {
    stageLength: { min: 70, max: 150 },
    clusters: { min: 2, max: 5 },
    clusterSpacing: { min: 20, max: 40 },
    fluidIntensity: { min: 15, max: 32 },
    proppantIntensity: { min: 1.0, max: 2.8 },
    maxClusterCoverageRatio: 0.8,
  },
  coalbed: {
    stageLength: { min: 40, max: 90 },
    clusters: { min: 2, max: 5 },
    clusterSpacing: { min: 12, max: 25 },
    fluidIntensity: { min: 10, max: 28 },
    proppantIntensity: { min: 0.6, max: 2.0 },
    maxClusterCoverageRatio: 0.85,
  },
};

const clamp = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

const levelStyle = (score: number) => (
  score >= 82
    ? { label: '推荐', color: '#1E8449', bg: 'rgba(30,132,73,0.14)' }
    : score >= 65
      ? { label: '需提高', color: '#D4770C', bg: 'rgba(212,119,12,0.14)' }
      : { label: '高风险', color: '#C0392B', bg: 'rgba(192,57,43,0.12)' }
);

const rangeMid = (r: Range) => (r.min + r.max) / 2;
const scaleRange = (r: Range, minFactor: number, maxFactor: number): Range => ({
  min: Math.round(r.min * minFactor * 10) / 10,
  max: Math.round(r.max * maxFactor * 10) / 10,
});

const applyIntent = (
  base: Omit<ScoringTemplate, 'reservoirType' | 'intent' | 'label'>,
  intent: DesignIntent
): Omit<ScoringTemplate, 'reservoirType' | 'intent' | 'label'> => {
  if (intent === 'dense_cut') {
    return {
      ...base,
      stageLength: scaleRange(base.stageLength, 0.75, 0.9),
      clusters: { min: base.clusters.min + 1, max: base.clusters.max + 2 },
      clusterSpacing: scaleRange(base.clusterSpacing, 0.65, 0.9),
      fluidIntensity: scaleRange(base.fluidIntensity, 1.05, 1.15),
      proppantIntensity: scaleRange(base.proppantIntensity, 1.05, 1.15),
      maxClusterCoverageRatio: Math.min(0.96, base.maxClusterCoverageRatio + 0.08),
    };
  }
  if (intent === 'long_fracture') {
    return {
      ...base,
      stageLength: scaleRange(base.stageLength, 1.0, 1.25),
      clusters: { min: Math.max(1, base.clusters.min - 1), max: Math.max(2, base.clusters.max - 1) },
      clusterSpacing: scaleRange(base.clusterSpacing, 1.1, 1.25),
      fluidIntensity: scaleRange(base.fluidIntensity, 1.15, 1.35),
      proppantIntensity: scaleRange(base.proppantIntensity, 1.05, 1.2),
      maxClusterCoverageRatio: Math.max(0.75, base.maxClusterCoverageRatio - 0.08),
    };
  }
  if (intent === 'cost_control') {
    return {
      ...base,
      stageLength: scaleRange(base.stageLength, 1.05, 1.3),
      clusters: { min: Math.max(1, base.clusters.min - 1), max: Math.max(2, base.clusters.max - 2) },
      clusterSpacing: scaleRange(base.clusterSpacing, 1.15, 1.35),
      fluidIntensity: scaleRange(base.fluidIntensity, 0.75, 0.95),
      proppantIntensity: scaleRange(base.proppantIntensity, 0.75, 0.95),
      maxClusterCoverageRatio: Math.max(0.75, base.maxClusterCoverageRatio - 0.06),
    };
  }
  if (intent === 'risk_control') {
    return {
      ...base,
      stageLength: scaleRange(base.stageLength, 0.72, 0.9),
      clusters: { min: Math.max(2, base.clusters.min - 1), max: Math.max(3, base.clusters.max - 2) },
      clusterSpacing: scaleRange(base.clusterSpacing, 1.05, 1.25),
      fluidIntensity: scaleRange(base.fluidIntensity, 0.72, 0.92),
      proppantIntensity: scaleRange(base.proppantIntensity, 0.68, 0.9),
      maxClusterCoverageRatio: Math.max(0.7, base.maxClusterCoverageRatio - 0.1),
    };
  }
  return base;
};

export const inferStageDesignIntent = (stage: FracStageLike, evals: FracEvalLike[] = []): DesignIntent => {
  const rows = evals.filter(row => row.bottom > stage.top_perf && row.top < stage.bottom_perf);
  if (!rows.length) return 'balanced';
  const ranks = rows.map(row => GRADE_RANK[row.grade] ?? 3);
  const averageRank = ranks.reduce((sum, rank) => sum + rank, 0) / ranks.length;
  const spread = Math.max(...ranks) - Math.min(...ranks);
  const averageScore = rows.filter(row => Number.isFinite(row.score)).length
    ? rows.reduce((sum, row) => sum + Number(row.score ?? 0), 0) / rows.length
    : null;
  if (ranks.includes(4) || spread >= 2 || averageRank >= 3.25 || (averageScore !== null && averageScore < 0.45)) return 'risk_control';
  if (averageRank <= 1.6 && spread <= 1 && (averageScore === null || averageScore >= 0.68)) return 'dense_cut';
  if (averageRank <= 2.5 && spread === 0 && (averageScore === null || averageScore >= 0.55)) return 'long_fracture';
  return 'balanced';
};

export const buildScoringTemplate = (config?: Partial<ScoringConfig>): ScoringTemplate => {
  const reservoirType = config?.reservoirType || 'shale';
  const intent = config?.intent || 'balanced';
  const base = applyIntent(BASE_TEMPLATES[reservoirType], intent);
  const custom = config?.custom || {};
  const guard = RESERVOIR_GUARDRAILS[reservoirType];
  const clusterSpacing = custom.clusterSpacing || {
    min: Math.max(base.clusterSpacing.min, guard.minEngineeringClusterSpacing),
    max: Math.max(base.clusterSpacing.max, guard.minEngineeringClusterSpacing + 2),
  };
  return {
    reservoirType,
    intent,
    label: `测井解释约束 · ${INTENT_LABELS[intent]}`,
    ...base,
    ...custom,
    clusterSpacing,
    maxClusterCoverageRatio: Math.min(
      custom.maxClusterCoverageRatio ?? base.maxClusterCoverageRatio,
      guard.maxEngineeringCoverageRatio
    ),
  };
};

export const getTemplateBasis = (config?: Partial<ScoringConfig>): TemplateBasis[] => {
  const reservoirType = config?.reservoirType || 'shale';
  const intent = config?.intent || 'balanced';
  const customNote = config?.custom
    ? '用户已启用自定义参考区间，当前评分以用户输入模板为准，经验默认值仅作为初始建议。'
    : '未启用自定义区间时，采用内置经验模板作为初始建议，后续应结合区块历史施工和产量数据标定。';
  return [
    ...RESERVOIR_BASIS[reservoirType],
    { title: '设计目标依据', detail: INTENT_BASIS[intent] },
    { title: '模板使用说明', detail: customNote },
  ];
};

const rangeScore = (value: number, range: Range) => {
  if (value >= range.min && value <= range.max) return 100;
  const mid = rangeMid(range);
  const tolerance = Math.max(1, range.max - range.min);
  const distance = value < range.min ? range.min - value : value - range.max;
  const relative = distance / Math.max(tolerance, mid * 0.35);
  return clamp(100 - relative * 70);
};

const rangeIssue = (value: number, range: Range, score: number, low: string, high: string) => {
  if (score >= 96) return null;
  if (value < range.min) return low;
  if (value > range.max) return high;
  return null;
};

export const scoreStageCluster = (
  stage: FracStageLike,
  design: FracDesignLike,
  evals: FracEvalLike[] = [],
  config?: Partial<ScoringConfig>
): StageCheck => {
  const adaptiveIntent = config?.custom ? config.intent : inferStageDesignIntent(stage, evals);
  const adaptiveConfig = { ...config, intent: adaptiveIntent };
  const template = buildScoringTemplate(adaptiveConfig);
  const stageLength = Math.max(0, stage.bottom_perf - stage.top_perf);
  const clusters = Math.max(1, Number(design.cluster_per_stage || 1));
  const clusterSpacing = Number(design.cluster_spacing || 0);
  const clusterSpan = clusters > 1 ? (clusters - 1) * clusterSpacing : 0;
  const fluidIntensity = stageLength > 0 ? stage.fluid_volume / stageLength : 0;
  const proppantIntensity = stageLength > 0 ? stage.proppant_mass / stageLength : 0;
  const gradeMix = Array.from(new Set(
    evals
      .filter((e) => e.bottom > stage.top_perf && e.top < stage.bottom_perf)
      .map((e) => e.grade || 'III')
  )).sort((a, b) => (GRADE_RANK[a] ?? 9) - (GRADE_RANK[b] ?? 9));

  const issues: string[] = [];
  const suggestions: string[] = [];

  const stageLengthScore = rangeScore(stageLength, template.stageLength);
  const clusterCountScore = rangeScore(clusters, template.clusters);
  const spacingScore = rangeScore(clusterSpacing, template.clusterSpacing);
  const coverageScore = clusterSpan <= stageLength * template.maxClusterCoverageRatio
    ? 100
    : clamp(100 - ((clusterSpan / Math.max(stageLength, 1)) - template.maxClusterCoverageRatio) * 180);
  let clusterSpacingScore = clamp(clusterCountScore * 0.35 + spacingScore * 0.45 + coverageScore * 0.2);
  let fluidScore = rangeScore(fluidIntensity, template.fluidIntensity);
  let propScore = rangeScore(proppantIntensity, template.proppantIntensity);
  let sweetSpotScore = evals.length ? 100 : 80;
  const stressScore: number | null = null;
  const guard = RESERVOIR_GUARDRAILS[template.reservoirType];

  const stageLengthIssue = rangeIssue(stageLength, template.stageLength, stageLengthScore, '段长低于工程推荐范围', '段长高于工程推荐范围');
  if (stageLengthIssue) {
    issues.push(stageLengthIssue);
    suggestions.push(`建议段长控制在 ${template.stageLength.min}-${template.stageLength.max} m，并结合储层连续性与应力分异调整段界。`);
  }

  const clusterIssue = rangeIssue(clusters, template.clusters, clusterCountScore, '簇数低于工程推荐范围', '簇数高于工程推荐范围');
  if (clusterIssue) {
    issues.push(clusterIssue);
    suggestions.push(`建议每段布置 ${template.clusters.min}-${template.clusters.max} 簇，并根据段内品质差异校核有效簇数。`);
  }

  const spacingIssue = rangeIssue(clusterSpacing, template.clusterSpacing, spacingScore, '簇间距低于工程推荐范围', '簇间距高于工程推荐范围');
  if (spacingIssue) {
    issues.push(spacingIssue);
    suggestions.push(`建议簇间距控制在 ${template.clusterSpacing.min}-${template.clusterSpacing.max} m，并校核应力阴影与簇间进液均衡性。`);
  }

  if (clusterSpacing > 0 && clusterSpacing < guard.minEngineeringClusterSpacing) {
    clusterSpacingScore = Math.min(clusterSpacingScore, 58);
    issues.push('簇间距低于工程下限');
    suggestions.push(`结合应力阴影与簇间进液均衡要求，簇间距宜不小于 ${guard.minEngineeringClusterSpacing} m。`);
  }

  if (clusterSpan > stageLength * template.maxClusterCoverageRatio) {
    clusterSpacingScore = Math.min(clusterSpacingScore, 62);
    issues.push('簇群长度占段长比例偏高');
    suggestions.push('建议减少簇数、调大段长或缩短簇群覆盖范围，避免两端无施工余量。');
  }

  if (gradeMix.includes('IV')) {
    sweetSpotScore -= 35;
    issues.push('段内包含 IV 类甜点');
    suggestions.push('建议跳过 IV 类差储层，或调整段界避开低品质区。');
  } else if (gradeMix.length >= 3) {
    sweetSpotScore -= 24;
    issues.push('甜点等级变化较大');
    suggestions.push('建议把段界放在甜点突变位置，使同段储层品质更一致。');
  } else if (gradeMix.length === 2) {
    sweetSpotScore -= 10;
    issues.push('甜点等级略有变化');
    suggestions.push('建议优先把簇布置在高甜点区。');
  } else if (!evals.length) {
    suggestions.push('方案管理页暂未加载甜点剖面，详细甜点一致性请进入段表页查看。');
  }

  if (template.intent === 'dense_cut' && (gradeMix.includes('III') || gradeMix.includes('IV'))) {
    sweetSpotScore -= 10;
    issues.push('密切割目标与段内甜点质量不完全匹配');
    suggestions.push('密切割更适合 I-II 类连续甜点段；III-IV 类混合段宜先调整段界，或采用保守布簇与泵注参数。');
  }

  const fluidIssue = rangeIssue(fluidIntensity, template.fluidIntensity, fluidScore, '单米液量低于工程推荐范围', '单米液量高于工程推荐范围');
  if (fluidIssue) {
    issues.push(fluidIssue);
    suggestions.push(`建议单米液量控制在 ${template.fluidIntensity.min}-${template.fluidIntensity.max} m³/m，并结合施工压力与储层吸液能力校核。`);
  }
  const propIssue = rangeIssue(proppantIntensity, template.proppantIntensity, propScore, '单米砂量低于工程推荐范围', '单米砂量高于工程推荐范围');
  if (propIssue) {
    issues.push(propIssue);
    suggestions.push(`建议单米砂量控制在 ${template.proppantIntensity.min}-${template.proppantIntensity.max} t/m，并校核携砂能力与砂堵风险。`);
  }

  if (fluidIntensity > guard.maxFluidIntensity) {
    fluidScore = Math.min(fluidScore, 55);
    issues.push('单米液量超过工程警戒值');
    suggestions.push(`综合施工压力、供液能力和水资源约束，单米液量宜不超过 ${guard.maxFluidIntensity} m³/m。`);
  }
  if (proppantIntensity > guard.maxProppantIntensity) {
    propScore = Math.min(propScore, 55);
    issues.push('单米砂量超过工程警戒值');
    suggestions.push(`综合支撑剂输送能力与砂堵风险，单米砂量宜不超过 ${guard.maxProppantIntensity} t/m。`);
  }

  const breakdown = {
    stageLength: clamp(stageLengthScore),
    clusterSpacing: clamp(clusterSpacingScore),
    sweetSpot: clamp(sweetSpotScore),
    stress: stressScore,
    fluidSand: clamp(fluidScore * 0.5 + propScore * 0.5),
  };
  const weights: Record<DesignIntent, [number, number, number, number]> = {
    balanced: [.25, .30, .20, .25], dense_cut: [.15, .35, .25, .25], long_fracture: [.20, .20, .20, .40],
    risk_control: [.25, .25, .35, .15], cost_control: [.25, .25, .20, .30],
  };
  const [stageWeight, clusterWeight, geologyWeight, fluidWeight] = weights[template.intent];
  const weightedScore = breakdown.stageLength * stageWeight + breakdown.clusterSpacing * clusterWeight + breakdown.sweetSpot * geologyWeight + breakdown.fluidSand * fluidWeight;
  const score = clamp(weightedScore);

  if (!issues.length) {
    issues.push('当前段簇参数未发现明显工程偏差');
    suggestions.push('可进入压裂模拟，进一步验证缝长、液效和净压力。');
  }

  return {
    score,
    ...levelStyle(score),
    issues,
    suggestions: Array.from(new Set(suggestions)).slice(0, 4),
    gradeMix: gradeMix.length ? gradeMix : ['III'],
    stageLength,
    clusterSpan,
    fluidIntensity,
    proppantIntensity,
    template,
    basis: getTemplateBasis(config),
    breakdown,
  };
};

export const scoreFracPlan = (
  design: FracDesignLike,
  evals: FracEvalLike[] = [],
  config?: Partial<ScoringConfig>,
  stageConfigs?: Record<number, Partial<ScoringConfig> | undefined>
): PlanScore => {
  const stages = (design.stages || []).slice().sort((a, b) => a.stage_number - b.stage_number);
  const checks = stages.map((stage) => scoreStageCluster(stage, design, evals, stageConfigs?.[stage.stage_number] || config));
  const intentDistribution = checks.reduce<Partial<Record<DesignIntent, number>>>((result, check) => {
    result[check.template.intent] = (result[check.template.intent] || 0) + 1;
    return result;
  }, {});
  const dominantIntent = (Object.entries(intentDistribution).sort((a, b) => b[1] - a[1])[0]?.[0] as DesignIntent | undefined) || config?.intent || 'balanced';
  const template = buildScoringTemplate({ ...config, intent: dominantIntent });

  if (!checks.length) {
    return {
      score: 0,
      ...levelStyle(0),
      checks,
      breakdown: { stageLength: 0, clusterSpacing: 0, sweetSpot: 0, stress: null, fluidSand: 0 },
      summary: ['暂无分段数据，无法评分。'],
      template,
      intentDistribution,
    };
  }

  const avg = (key: keyof Omit<ScoreBreakdown, 'stress'>) =>
    clamp(checks.reduce((sum, c) => sum + c.breakdown[key], 0) / checks.length);
  const score = clamp(checks.reduce((sum, c) => sum + c.score, 0) / checks.length);
  const issueCounts = new Map<string, number>();
  checks.forEach((check) => {
    check.issues.forEach((issue) => issueCounts.set(issue, (issueCounts.get(issue) || 0) + 1));
  });
  const summary = Array.from(issueCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([issue, count]) => `${issue}(${count}段)`);

  return {
    score,
    ...levelStyle(score),
    checks,
    breakdown: {
      stageLength: avg('stageLength'),
      clusterSpacing: avg('clusterSpacing'),
      sweetSpot: avg('sweetSpot'),
      stress: null,
      fluidSand: avg('fluidSand'),
    },
    summary: summary.length ? summary : ['整体参数满足当前工程推荐范围。'],
    template,
    intentDistribution,
  };
};
